import {
  Chat,
  isChatError,
  MaybeOptimisticChatTextMessage,
} from "@lazarentertainment/public-chat-web"
import { ControlledMenu, MenuItem, useMenuState } from "@szhsin/react-menu"
import VerticalEllipsesIcon from "assets/icons/verticalEllipsis.svg"
import { useRef } from "react"
import { FormattedMessage } from "react-intl"
import styled from "styled-components"

import { ChatView } from "../../constants"

const MenuButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;

  &:hover .message-menu-icon {
    opacity: 1;
  }
`

const MenuIcon = styled(VerticalEllipsesIcon)`
  height: 18px;
  margin: 5px 3px;
  opacity: 0.7;
  fill: ${({ selected, theme }) =>
    selected ? theme.colors.accent : theme.colors.darkMed};

  &[data-uiview="${ChatView.OVERLAY}"] {
    fill: ${(props) => props.theme.colors.white};
  }
`

const Container = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: row;

  ${({ $open: open }) => open && `visibility: visible !important;`}

  * {
    text-shadow: none !important;
  }
`

export const ModerationToolsMenuButton = ({
  showModerationTools,
  banUser,
  uiView,
  deleteChatMessage,
  message,
}: {
  showModerationTools: boolean
  banUser: Chat["banUser"]
  deleteChatMessage: Chat["deleteMessage"]
  message: MaybeOptimisticChatTextMessage
  uiView: ChatView
}) => {
  const menuButtonAnchorRef = useRef(null)
  const [{ state: menuState }, toggleReactionPickerMenu] = useMenuState()
  const skipOpen = useRef(false)

  if (!showModerationTools) {
    return null
  }

  return (
    <Container
      className="chat-message-menu-option"
      $open={menuState === "open"}
    >
      <ControlledMenu
        anchorRef={menuButtonAnchorRef}
        state={menuState}
        onClose={({ reason }) => {
          if (reason === "blur") {
            // NOTE: Due to the issue outlined in https://github.com/szhsin/react-menu/issues/56, this
            // workaround has been added in order to fix a bug where clicking the menu button while
            // the menu is already open prevents the menu from closing properly.
            skipOpen.current = true
            setTimeout(() => {
              skipOpen.current = false
            }, 300)
          }
          toggleReactionPickerMenu(false)
        }}
        position="auto"
        align="start"
        direction="top"
      >
        <MenuItem
          onClick={() => {
            banUser({
              userId: message.userId,
              displayName: message.displayName,
              sourceName: message.sourceName,
            })
          }}
          data-robot="ban-user"
        >
          <FormattedMessage
            id="chat.moderator.banUser"
            defaultMessage="Ban user"
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteChatMessage(message).catch((error: unknown) => {
              if (isChatError(error)) {
                /* ignore, this is handled elsewhere */
                return
              }

              throw error
            })
          }}
          data-robot="delete-message"
        >
          <FormattedMessage
            id="chat.moderator.deleteMessage"
            defaultMessage="Delete Message"
          />
        </MenuItem>
      </ControlledMenu>
      <MenuButton
        ref={menuButtonAnchorRef}
        onClick={() => !skipOpen.current && toggleReactionPickerMenu(true)}
        data-robot="open-ban-modal"
      >
        <MenuIcon className="message-menu-icon" data-uiView={uiView} />
      </MenuButton>
    </Container>
  )
}
